// SHORTAGE_FOLLOWUP (ICT-13): when Fulfillment marks a receipt Received
// Short, Ace sends Proxy an in-app notification (contexts/TeamMessagesContext)
// and turns it into this task: chase the solicitor for the rest. Each step
// after that is reported back to Fulfillment the same way.
//
// The ordered / received / short numbers come from the Proxy job's own
// shortageLines (see mockProxyJobs.ts).
import { type ProxyJob, PROXY_SEED_TODAY } from '../mockProxyJobs';
import type { TeamMessage } from '../../contexts/TeamMessagesContext';
import { hasOnlySkippedMaterial } from '../../utils/proxyQueue';
import { isSkippedMaterial, evaluatePod } from '../demoShortages';
import { resolveOutreachContact, contactNote } from '../proxyFallbackSolicitors';
import type {
  ProxyTask, ProxyWorkflowContext, ProxyWorkflowDefinition,
  ProxyExtractedField, ProxyReviewPanelSchema, ProxyPanelContext,
} from '../proxyWorkflowTypes';

// A job's SHORTAGE_FOLLOWUP task is done once ACE has logged the Material
// Short MIC follow-up for it — at or after the latest request from the
// Follow Ups tracker, so a due re-chase opens a fresh task.
export function shortageResolved(job: ProxyJob): boolean {
  const requested = job.shortageFollowupRequestedAt || '';
  return job.followUpHistory.some(r => r.type === 'Material Short' && r.timestamp >= requested);
}

function actionableLines(job: ProxyJob) {
  return (job.shortageLines || []).filter(l => !isSkippedMaterial(l.materialType));
}

export function buildShortageTask(job: ProxyJob, message?: TeamMessage): ProxyTask {
  if (!message) {
    return {
      id: `shortage-${job.jobId}`,
      workflowType: 'SHORTAGE_FOLLOWUP',
      jobId: job.jobId,
      title: `${job.issuer} - Material shortage follow-up`,
      subtitle: `Job ${job.jobId}`,
      reason: 'The last shortage follow-up to the solicitor is due again. The shorted material still hasn\'t arrived.',
      source: 'job_signal',
      sourceLabel: 'Follow Ups tracker',
      createdAt: job.shortageFollowupRequestedAt || PROXY_SEED_TODAY,
      confidence: 97,
    };
  }
  return {
    id: `shortage-${job.jobId}`,
    workflowType: 'SHORTAGE_FOLLOWUP',
    jobId: job.jobId,
    title: `${job.issuer} - Material shortage follow-up`,
    subtitle: `Job ${job.jobId}`,
    reason: 'Fulfillment marked this delivery Received Short. The material received is below what was ordered.',
    source: 'fulfillment_escalation',
    sourceLabel: 'Fulfillment notification',
    sourceDetail: {
      rows: [
        { label: 'From', value: 'Fulfillment (Receiving)' },
        ...(message.scan ? [{ label: 'Scan #', value: message.scan }] : []),
        { label: 'Message', value: message.body },
        { label: 'Sent', value: message.at },
      ],
    },
    createdAt: message.at.slice(0, 10),
    // A confirmed hand-off, not a guess: Fulfillment already counted it.
    confidence: 97,
  };
}

// One task per Received Short hand-off from Fulfillment, until the chase is
// logged; after that the Follow Ups tracker asks for the next one.
function buildTasks({ jobs, teamMessages = [] }: ProxyWorkflowContext): ProxyTask[] {
  const tasks: ProxyTask[] = [];
  const requested = jobs.filter(j => !!j.shortageFollowupRequestedAt && !shortageResolved(j));
  requested.forEach(job => tasks.push(buildShortageTask(job)));
  for (const message of teamMessages) {
    if (message.kind !== 'received-short' || message.to !== 'cary' || !message.jobId) continue;
    const job = jobs.find(j => j.jobId === message.jobId);
    if (!job || !job.shortageLines?.length) continue;
    if (requested.includes(job) || tasks.some(t => t.jobId === job.jobId)) continue;
    if (hasOnlySkippedMaterial(job)) continue; // ICT-13 §6.i: SMRF/VIF-only shortages aren't followed up
    if (shortageResolved(job)) continue;
    tasks.push(buildShortageTask(job, message));
  }
  return tasks;
}

function defaultEdits(): Record<string, any> {
  return {}; // nothing here is human-editable — the panel's Edit button hides itself when no field declares `editable`
}

function buildPanel({ job }: ProxyPanelContext): ProxyReviewPanelSchema {
  const lines = actionableLines(job);
  const totalOrdered = lines.reduce((s, l) => s + l.ordered, 0);
  const totalReceived = lines.reduce((s, l) => s + l.received, 0);
  const totalShort = lines.reduce((s, l) => s + l.shortage, 0);

  // POD eligibility (utils/demoShortages.ts's evaluatePod, unit-tested in
  // demoShortages.test.ts) is per-line — a job is only eligible if every
  // actionable line clears both bounds; the first line that doesn't drives
  // the escalate-to-manager reason shown.
  const ineligibleLine = lines.map(l => ({ line: l, verdict: evaluatePod(l) })).find(r => !r.verdict.eligible);
  const podEligible = !ineligibleLine;
  const podReason = ineligibleLine
    ? `${ineligibleLine.line.materialType}: ${ineligibleLine.verdict.reason}`
    : lines.length
      ? `All ${lines.length} line(s) are 100 pages or less and 50 shorted or less.`
      : 'No actionable shortage lines.';

  const recipient = resolveOutreachContact(job);

  const extractedFields: ProxyExtractedField[] = [
    { key: 'issuer', label: 'Issuer', display: job.issuer },
    { key: 'jobId', label: 'Job #', display: job.jobId },
    { key: 'cusip', label: 'CUSIP', display: job.cusip },
    { key: 'solicitor', label: 'Solicitor', display: job.solicitor || 'Issuer (no solicitor on record)' },
    ...lines.map((l, i) => ({
      key: `line-${i}`,
      label: l.materialType,
      display: `${l.received} of ${l.ordered} received (short ${l.shortage}, ${l.pages}pp)`,
    })),
  ];

  const linesText = lines.map(l => `${l.materialType}: received ${l.received} of ${l.ordered} ordered (short ${l.shortage})`).join('\n');
  const outreachSubject = `Material shortage - ${job.issuer} (Job ${job.jobId})`;
  // The POD guideline stays internal: externally the solicitor is asked to
  // send the shorted material, or to pay for POD (ICT-13).
  const outreachBody = `We show a shortage on the material received for the above job:\n\n${linesText}\n\nPlease send the outstanding material and advise on the expected ship date. If you would like us to print the shorted material on demand, please confirm approval of the printing charges.`;
  const micBody = `${totalShort} short across ${lines.length} line(s). Shortage follow-up sent to the solicitor. Internal POD guideline: ${podEligible ? 'within' : 'outside, so manager approval is needed for no-cost POD'}.`;

  return {
    extractedFields,
    calculation: {
      title: 'Shortage & POD eligibility',
      ruleText: 'Chase the solicitor for the shorted material. POD guideline (internal only, ICT-13 section 7.i): 100 pages or less and 50 shorted or less. The solicitor is always asked to send or pay first; no-cost POD is an ICT judgment call if they can\'t or won\'t send. SMRF/VIF lines are excluded from follow-up (ICT-13 section 6.i).',
      rows: [
        { label: 'Total ordered', value: totalOrdered.toLocaleString() },
        { label: 'Total received', value: totalReceived.toLocaleString() },
      ],
      totalRow: { label: 'Total short', value: totalShort.toLocaleString() },
      deltaRow: {
        label: 'Internal POD guideline',
        value: podEligible ? `Within guideline, so no-cost POD is ICT's call if the solicitor can't send. ${podReason}` : `Outside guideline, so no-cost POD needs manager approval. ${podReason}`,
        tone: podEligible ? 'muted' : 'amber',
      },
    },
    drafts: recipient ? [
      {
        key: 'outreach', icon: '📧', label: `Shortage outreach → solicitor (${recipient.name})`,
        to: recipient.email, cc: ['proxymaterial@betanxt.com'],
        note: contactNote(recipient),
        subject: outreachSubject, body: outreachBody,
      },
    ] : [],
    approveSummary: [
      recipient ? 'Send the shortage outreach email' : 'No contact on file, so contact tracing is needed (see Exceptions) and no email is sent',
      'Log a MIC follow-up (Material Short)',
      'Let Fulfillment know the solicitor has been chased',
      'Move this task to Done.',
    ],
    approveConfirmation: recipient
      ? `Shortage outreach sent to <strong>${recipient.name}</strong> and logged as Material Short for Job ${job.jobId}.`
      : `Shortage logged as Material Short for Job ${job.jobId}. There's no contact on file, so no outreach was sent.`,
    outboundActions: [
      ...(recipient ? [{ key: 'outreach', followUpType: 'shortage_outreach', recipientEmail: recipient.email, ccEmails: ['proxymaterial@betanxt.com'], subject: outreachSubject, bodyPreview: outreachBody }] : []),
      { key: 'mic', followUpType: 'Material Short', recipientEmail: 'mic@betanxt.com', subject: `Material shortage documented for Job ${job.jobId}`, bodyPreview: micBody },
    ],
    teamUpdate: {
      kind: 'shortage-update',
      title: `Shortage chased: ${job.issuer}`,
      body: recipient
        ? `Proxy has asked ${recipient.name} for the ${totalShort} short set${totalShort === 1 ? '' : 's'} on Job ${job.jobId}. We'll let you know when they're on the way.`
        : `Proxy is finding a contact for the ${totalShort} short set${totalShort === 1 ? '' : 's'} on Job ${job.jobId}. We'll let you know when they're on the way.`,
    },
  };
}

export const SHORTAGE_FOLLOWUP_WORKFLOW: ProxyWorkflowDefinition = {
  type: 'SHORTAGE_FOLLOWUP',
  label: 'Shortage Follow-Up',
  noun: 'a shortage follow-up',
  trigger: 'Fulfillment marks a receipt Received Short, which sends Proxy an in-app notification.',
  source: 'Fulfillment\'s Received Short notification, for a job with known shortage lines.',
  steps: ['Source & extraction', 'Shortage & POD eligibility', 'Draft & approve'],
  buildTasks,
  defaultEdits,
  buildPanel,
};
